import { AfterViewChecked, Directive, ElementRef, Input } from '@angular/core'
import { LucideCheck, LucideIconData, LucideLoaderCircle } from '@lucide/angular'

@Directive({
  selector: '[busy-button]',
  standalone: false,
})
export class BusyButtonDirective implements AfterViewChecked {
  @Input() busy = false
  @Input() done = false
  @Input('glyph-size') glyphSize = '1.1rem'
  private initialized = false
  private originalInnerHtml: string = null
  private pixelWidth: string = null
  private originalWidth: string = null
  private pixelHeight: string = null
  private originalHeight: string = null
  private originalPosition: string = null

  constructor(private el: ElementRef) {}

  // The directive replaces the button's innerHTML rather than rendering a template, so the Lucide
  // glyph is serialised from the icon's own node data instead of using its <svg lucideX> component.
  private glyphHtml(icon: LucideIconData, spin: boolean) {
    const nodes = icon.node
      .map(([tag, attrs]) => {
        const serialized = Object.entries(attrs)
          .filter(([name]) => name !== 'key')
          .map(([name, value]) => `${name}="${value}"`)
          .join(' ')
        return `<${tag} ${serialized} />`
      })
      .join('')
    return `
    <span style="position:absolute;top:50%;left:50%;transform:translate(-50%, -50%);font-size:${this.glyphSize};">
      <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
        class="lucide lucide-${icon.name}${spin ? ' lucide-spin' : ''}">${nodes}</svg>
    </span>
    `
  }

  private busyHtml() {
    return this.glyphHtml(LucideLoaderCircle.icon, true)
  }

  private doneHtml() {
    return this.glyphHtml(LucideCheck.icon, false)
  }

  ngAfterViewChecked() {
    if (!this.initialized && this.el.nativeElement.offsetWidth > 0) {
      this.originalInnerHtml = this.el.nativeElement.innerHTML
      this.pixelWidth = this.el.nativeElement.offsetWidth
      this.originalWidth = this.el.nativeElement.style.width
      this.pixelHeight = this.el.nativeElement.offsetHeight
      this.originalHeight = this.el.nativeElement.style.height
      this.originalPosition = this.el.nativeElement.style.position
      this.initialized = true

      this.ngOnChanges()
    }
  }

  ngOnChanges() {
    // This function is invoked whenever one of the @Inputs change for this directive.
    // We use this event to compute the appropriate HTML content for the control to
    // indicate activity or doneness.

    if (!this.initialized) {
      // If the view has not been initialized yet, we cannot do anything.
      return
    }

    if (this.busy) {
      this.el.nativeElement.style.width = `${this.pixelWidth}px`
      this.el.nativeElement.style.height = `${this.pixelHeight}px`
      this.el.nativeElement.style.position = 'relative'
      this.el.nativeElement.innerHTML = this.busyHtml()
    } else if (this.done) {
      this.el.nativeElement.style.width = `${this.pixelWidth}px`
      this.el.nativeElement.style.height = `${this.pixelHeight}px`
      this.el.nativeElement.style.position = 'relative'
      this.el.nativeElement.innerHTML = this.doneHtml()
    } else {
      this.el.nativeElement.style.width = this.originalWidth
      this.el.nativeElement.style.height = this.originalHeight
      this.el.nativeElement.style.position = this.originalPosition
      this.el.nativeElement.innerHTML = this.originalInnerHtml
    }
  }
}
